<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3>
            <i class="fa fa-fw fa-plug"></i> {{ integration.name || _('Integration') }}
            <span :class="statusClass(integration.status)" v-if="integration.status">{{ statusLabel(integration.status) }}</span>
          </h3>
          <p class="text-muted" v-if="integration.partner_name">{{ _('Partner') }}: {{ integration.partner_name }}</p>
        </div>
      </div>
      <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-sm-6 leftcol">
                <div class="form-group">
                  <label for="integrationName">{{ _('Name') }}</label>
                  <input class="form-control" id="integrationName" type="text" v-model="integration.name" />
                </div>
                <div class="form-group">
                  <label for="integrationDescription">{{ _('Description') }}</label>
                  <textarea class="form-control" id="integrationDescription" rows="4" v-model="integration.description"></textarea>
                  <p class="help-block">{{ _('Shops see the name and the description when they give consent.') }}</p>
                </div>
                <div class="form-group">
                  <label for="integrationWebsite">{{ _('Website') }}</label>
                  <input class="form-control" id="integrationWebsite" type="text" v-model.trim="integration.website" placeholder="https://" />
                </div>
                <p class="text-muted" v-if="integration.created_at">{{ _('Created') }}: {{ formatApiTime(integration.created_at) }}</p>
              </div>
              <div class="col-sm-6 rightcol">
                <div class="form-group">
                  <label>{{ _('Scopes') }}</label>
                  <p class="help-block">{{ _('The most the integration may ask a shop for. Each shop decides what it grants.') }}</p>
                  <div class="checkbox" v-for="scope in partnerScopes" :key="scope.scope">
                    <label>
                      <input type="checkbox" :value="scope.scope" v-model="integration.scopes" />
                      {{ _(scope.label) }} <code>{{ scope.scope }}</code>
                    </label>
                  </div>
                  <div class="checkbox" v-for="scope in unknownScopes" :key="scope">
                    <label>
                      <input type="checkbox" :value="scope" v-model="integration.scopes" />
                      <code>{{ scope }}</code>
                    </label>
                  </div>
                </div>
                <div class="panel panel-default">
                  <div class="panel-heading">
                    <h4 class="panel-title">{{ _('Status') }}: <span :class="statusClass(integration.status)">{{ statusLabel(integration.status) }}</span></h4>
                  </div>
                  <div class="panel-body">
                    <p class="help-block">{{ integration.status === 'suspended' ? _('All of the integration\'s keys are refused until it is activated again.') : _('Suspending refuses all of the integration\'s keys. Shop connections are kept.') }}</p>
                    <button v-if="integration.status === 'suspended'" type="button" class="btn btn-success" v-on:click="setStatus('active')" :disabled="loading || saving"><i class="fa fa-fw fa-play"></i> {{ _('Activate') }}</button>
                    <button v-else type="button" class="btn btn-danger" v-on:click="setStatus('suspended')" :disabled="loading || saving || !integration.id"><i class="fa fa-fw fa-ban"></i> {{ _('Suspend') }}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('keys')" class="tab-pane active" id="keys" v-if="isTabActive('keys')" key="keys">
            <div class="partner-tab-buttons">
              <button type="button" class="btn btn-success" v-on:click="createKey" :disabled="!integration.id || saving"><i class="fa fa-fw fa-plus"></i> {{ _('Create key') }}</button>
            </div>
            <div class="partner-table-wrap" v-if="keys.length">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ _('Label') }}</th>
                    <th>{{ _('Key') }}</th>
                    <th>{{ _('Type') }}</th>
                    <th>{{ _('Created') }}</th>
                    <th>{{ _('Last used') }}</th>
                    <th>{{ _('Expires') }}</th>
                    <th>{{ _('Status') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="key in keys" :key="key.id" :class="{ 'text-muted': keyStatus(key) !== 'active' }">
                    <td>{{ key.label }}</td>
                    <td><code>{{ key.prefix }}…</code></td>
                    <td>
                      {{ keyKindLabel(key.kind) }}, {{ keyEnvLabel(key.env) }}
                      <div class="small text-muted" v-for="origin in keyOrigins(key)" :key="origin">{{ origin }}</div>
                    </td>
                    <td>
                      {{ formatApiTime(key.created_at) }}
                      <div class="small text-muted" v-if="key.created_by">{{ key.created_by }}</div>
                    </td>
                    <td>{{ formatApiTime(key.last_used_at) }}</td>
                    <td>{{ key.expires_at ? formatApiDate(key.expires_at) : _('Never') }}</td>
                    <td>
                      <span :class="statusClass(keyStatus(key))">{{ statusLabel(keyStatus(key)) }}</span>
                      <div class="small" v-if="key.revoked_at">{{ formatApiTime(key.revoked_at) }}</div>
                    </td>
                    <td class="text-right">
                      <button v-if="!key.revoked_at" type="button" class="btn btn-danger btn-sm" v-on:click="revokeKey(key)" :disabled="saving"><i class="fa fa-fw fa-ban"></i> {{ _('Revoke') }}</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted" v-else>{{ _('No keys yet.') }}</p>
          </div>
          <div v-bind="tabPanel('shops')" class="tab-pane active" id="shops" v-if="isTabActive('shops')" key="shops">
            <div class="partner-table-wrap" v-if="connections.length">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ _('Shop') }}</th>
                    <th>{{ _('Scopes') }}</th>
                    <th>{{ _('Connection') }}</th>
                    <th>{{ _('Status') }}</th>
                    <th>{{ _('Last used') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="connection in connections" :key="connection.id">
                    <td>
                      {{ connection.shop_name || connection.shop }}
                      <div class="small text-muted" v-if="connection.shop_name">{{ connection.shop }}</div>
                    </td>
                    <td>
                      <div v-for="scope in normalizeScopes(connection.scopes)" :key="scope" :title="scope">{{ scopeLabel(scope) }}</div>
                    </td>
                    <td>
                      {{ methodLabel(connection.method) }}
                      <div class="small text-muted" v-if="connection.requested_at">{{ _('Requested') }}: {{ formatApiTime(connection.requested_at) }}</div>
                      <div class="small text-muted" v-if="connection.requested_reason">{{ connection.requested_reason }}</div>
                      <div class="small text-muted" v-if="connection.decided_at">{{ _('Decided') }}: {{ formatApiTime(connection.decided_at) }}<span v-if="connection.decided_by_email"> ({{ connection.decided_by_email }})</span></div>
                    </td>
                    <td><span :class="statusClass(connection.status)">{{ statusLabel(connection.status) }}</span></td>
                    <td>{{ formatApiTime(connection.last_used_at) }}</td>
                    <td class="text-right">
                      <button v-if="connection.status === 'active' || connection.status === 'requested'" type="button" class="btn btn-danger btn-sm" v-on:click="revokeConnection(connection)" :disabled="saving"><i class="fa fa-fw fa-ban"></i> {{ _('Revoke') }}</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted" v-else>{{ _('No shops are connected yet.') }}</p>
          </div>
          <div v-bind="tabPanel('usage')" class="tab-pane active" id="usage" v-if="isTabActive('usage')" key="usage">
            <div class="usage-range">
              <div class="usage-range-picker">
                <DatePicker v-model="usageFrom" :shortLocale="shortLocale" :title="_('From')" :staticInput="true" :maxDate="usageTo" field="partnerusagefrom"></DatePicker>
              </div>
              <div class="usage-range-picker">
                <DatePicker v-model="usageTo" :shortLocale="shortLocale" :title="_('To')" :staticInput="true" :minDate="usageFrom" field="partnerusageto"></DatePicker>
              </div>
              <button type="button" class="btn btn-default" v-on:click="loadUsage" :disabled="usageLoading || !integration.id"><i :class="'fa fa-fw fa-' + (usageLoading ? 'cog fa-spin' : 'refresh')"></i> {{ _('Refresh') }}</button>
            </div>
            <h4>{{ _('Requests per day') }}</h4>
            <bar-chart v-if="chartLoaded" :key="chartKey" :chartdata="chartData" :options="chartOptions" style="height: 300px" />
            <p class="text-muted" v-else-if="!usageLoading">{{ _('No requests in the selected period.') }}</p>
            <div class="row">
              <div class="col-lg-6">
                <h4>{{ _('By endpoint') }}</h4>
                <div class="partner-table-wrap" v-if="usage.by_endpoint.length">
                  <table class="table table-striped table-condensed">
                    <thead>
                      <tr>
                        <th>{{ _('Endpoint') }}</th>
                        <th class="text-right">{{ _('Requests') }}</th>
                        <th class="text-right">{{ _('Errors') }}</th>
                        <th class="text-right">{{ _('Average time') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in usage.by_endpoint" :key="row.endpoint">
                        <td><code>{{ row.endpoint }}</code></td>
                        <td class="text-right">{{ formatCount(row.requests) }}</td>
                        <td class="text-right">{{ formatCount(row.errors) }}</td>
                        <td class="text-right">{{ formatMs(row.avg_ms) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p class="text-muted" v-else>{{ _('No requests.') }}</p>
              </div>
              <div class="col-lg-6">
                <h4>{{ _('By shop') }}</h4>
                <div class="partner-table-wrap" v-if="usage.by_shop.length">
                  <table class="table table-striped table-condensed">
                    <thead>
                      <tr>
                        <th>{{ _('Shop') }}</th>
                        <th class="text-right">{{ _('Requests') }}</th>
                        <th class="text-right">{{ _('Errors') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in usage.by_shop" :key="row.shop">
                        <td>{{ row.shop }}</td>
                        <td class="text-right">{{ formatCount(row.requests) }}</td>
                        <td class="text-right">{{ formatCount(row.errors) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p class="text-muted" v-else>{{ _('No requests.') }}</p>
              </div>
            </div>
            <h4>{{ _('Recent requests') }}</h4>
            <div class="partner-table-wrap" v-if="usage.recent.length">
              <table class="table table-striped table-condensed">
                <thead>
                  <tr>
                    <th>{{ _('Time') }}</th>
                    <th>{{ _('Request') }}</th>
                    <th class="text-right">{{ _('Status') }}</th>
                    <th class="text-right">{{ _('Duration') }}</th>
                    <th>{{ _('Shop') }}</th>
                    <th>{{ _('IP address') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in usage.recent" :key="index">
                    <td class="nowrap">{{ formatApiTime(row.ts) }}</td>
                    <td><code>{{ row.method }} {{ row.path }}</code></td>
                    <td class="text-right"><span :class="'label ' + httpStatusClass(row.status)">{{ row.status }}</span></td>
                    <td class="text-right">{{ formatMs(row.ms) }}</td>
                    <td>{{ row.shop }}</td>
                    <td>{{ row.ip }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted" v-else>{{ _('No requests.') }}</p>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="flex-row"></div>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled || !integration.id"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import { DateTime } from 'luxon'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import escapeHtml from '~/lib/escapehtml'
import partnerapi from '~/lib/partnerapi'
import { BarChart, DatePicker } from '#components'
import PartnerKeySecret from '~/components/modals/PartnerKeySecret.vue'

const USAGE_DAYS = 30

export default {
  mixins: [changewatcher, components, partnerapi],
  data () {
    return {
      activeTab: 'general',
      integration: this.emptyIntegration(),
      keys: [],
      connections: [],
      usage: this.emptyUsage(),
      usageFrom: null,
      usageTo: null,
      usageLoading: false,
      usageLoadedFor: 0,
      chartLoaded: false,
      chartKey: 0
    }
  },
  computed: {
    tabList () {
      const requested = this.connections.filter(c => c.status === 'requested').length
      return this.editorTabs([
        { id: 'general', label: this._('Overview') },
        { id: 'keys', label: this._('Keys') },
        { id: 'shops', label: this._('Shops'), badge: requested || undefined, badgeClass: 'btn-warning' },
        { id: 'usage', label: this._('Usage') }
      ])
    },
    requestedId () {
      return this.component && this.component.initData ? parseInt(this.component.initData.integrationId || 0, 10) : 0
    },
    unknownScopes () {
      return this.integration.scopes.filter(scope => !this.partnerScopes.some(s => s.scope === scope))
    },
    chartData () {
      const series = this.usage.series
      const dataset = (label, field, color) => ({
        label,
        backgroundColor: color,
        borderColor: color,
        data: series.map(row => Number(row[field]) || 0)
      })
      return {
        labels: series.map(row => this.formatSeriesDate(row.date)),
        datasets: [
          dataset(this._('OK'), 'ok', 'rgb(76,175,80)'),
          dataset(this._('Client errors'), 'client_errors', 'rgb(255,152,0)'),
          dataset(this._('Server errors'), 'server_errors', 'rgb(229,57,53)'),
          dataset(this._('Rate limited'), 'rate_limited', 'rgb(126,87,194)')
        ]
      }
    },
    chartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: { stacked: true },
          y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } }
        },
        plugins: {
          tooltip: { mode: 'index', intersect: false }
        }
      }
    }
  },
  methods: {
    emptyIntegration () {
      return {
        id: 0,
        partner_id: 0,
        partner_name: '',
        name: '',
        description: '',
        website: '',
        scopes: [],
        status: '',
        created_at: null
      }
    },
    emptyUsage () {
      return { series: [], by_endpoint: [], by_shop: [], recent: [] }
    },
    getSaveData () {
      return {
        name: (this.integration.name || '').trim(),
        description: this.integration.description || '',
        website: this.integration.website || '',
        scopes: [...this.integration.scopes]
      }
    },
    applyIntegration (data) {
      const integration = Object.assign(this.emptyIntegration(), data || {})
      this.keys = Array.isArray(integration.keys) ? integration.keys : []
      this.connections = Array.isArray(integration.connections) ? integration.connections : []
      delete integration.keys
      delete integration.connections
      integration.scopes = this.normalizeScopes(integration.scopes)
      for (const key of ['name', 'description', 'website']) {
        integration[key] = integration[key] || ''
      }
      this.integration = integration
      this.storeState()
    },
    integrationUrl (extra) {
      return this.baseDomain + this.shopId + '/admin/integrations/' + this.integration.id + '.json?apikey=' + this.apiKey + (extra || '')
    },
    async loadIntegration (id) {
      if (!id) {
        return
      }
      this.loading = true
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/admin/integrations/' + id + '.json?apikey=' + this.apiKey)
        this.applyIntegration(data)
      } catch (e) {
        this.showError(e, this._('Loading the integration failed.'))
      } finally {
        this.loading = false
      }
      if (this.isTabActive('usage') && this.usageLoadedFor !== this.integration.id) {
        this.loadUsage()
      }
    },
    // A PATCH with an operation, then the integration is loaded again. Returns the response data, or null.
    async patchIntegration (payload, successMessage) {
      this.saving = true
      try {
        const { data } = await axios.patch(this.integrationUrl(), payload)
        await this.loadIntegration(this.integration.id)
        this.EventBus.$emit('partnerIntegrationUpdate')
        this.EventBus.$emit('partnerUpdate')
        if (successMessage) {
          this.showNotification(successMessage)
        }
        return data || {}
      } catch (e) {
        this.showError(e, null, true)
        return null
      } finally {
        this.saving = false
      }
    },
    async save () {
      if (!this.integration.id) {
        return
      }
      const result = await this.patchIntegration({ operation: 'update', ...this.getSaveData() }, this._('Changes saved successfully!'))
      if (result) {
        this.saved = true
        setTimeout(() => { this.saved = false }, 1000)
      }
    },
    setStatus (status) {
      if (status === 'active') {
        this.patchIntegration({ operation: 'status', status }, this._('Status changed.'))
        return
      }
      this.showDialog({
        title: this._('Suspend %s?').replace('%s', this.integration.name),
        text: this._('All of the integration\'s keys are refused until it is activated again.'),
        buttons: [
          { title: this._('Cancel'), icon: 'ban', handler: () => {} },
          {
            title: this._('Suspend'),
            class: 'danger',
            icon: 'ban',
            handler: () => {
              this.patchIntegration({ operation: 'status', status }, this._('Status changed.'))
            }
          }
        ]
      })
    },
    keyKindLabel (kind) {
      if (kind === 'secret') {
        return this._('Secret key')
      }
      if (kind === 'publishable') {
        return this._('Publishable key')
      }
      return kind || ''
    },
    keyEnvLabel (env) {
      if (env === 'live') {
        return this._('Live')
      }
      if (env === 'test') {
        return this._('Test')
      }
      return env || ''
    },
    keyOrigins (key) {
      if (Array.isArray(key.allowed_origins)) {
        return key.allowed_origins
      }
      return this.normalizeScopes(key.allowed_origins)
    },
    keyStatus (key) {
      if (key.revoked_at) {
        return 'revoked'
      }
      const expires = this.parseApiTime(key.expires_at)
      if (expires && expires < DateTime.now()) {
        return 'expired'
      }
      return 'active'
    },
    createKey () {
      this.showDialog({
        title: this._('Create key'),
        fields: [
          { name: 'label', type: 'text', title: this._('Label'), placeholder: this._('For example: Production server') },
          {
            name: 'kind',
            type: 'select',
            title: this._('Type'),
            half: 'left',
            defaultValue: 'secret',
            options: [
              { value: 'secret', name: escapeHtml(this._('Secret key')) },
              { value: 'publishable', name: escapeHtml(this._('Publishable key')) }
            ]
          },
          {
            name: 'env',
            type: 'select',
            title: this._('Environment'),
            half: 'right',
            defaultValue: 'test',
            options: [
              { value: 'test', name: escapeHtml(this._('Test')) },
              { value: 'live', name: escapeHtml(this._('Live')) }
            ]
          },
          {
            type: 'info',
            text: escapeHtml(this._('Secret keys are for servers. Publishable keys are for browsers and only work for catalog, availability and cart calls from the allowed origins.')),
            hideIcon: true
          },
          {
            name: 'allowed_origins',
            type: 'textarea',
            title: this._('Allowed origins'),
            rows: 3,
            placeholder: 'https://www.example.com',
            help: this._('One origin per line.'),
            condition: 'kind',
            conditionValue: ['publishable']
          },
          { name: 'expires_at', type: 'date', title: this._('Expires'), help: this._('Leave empty for a key that doesn\'t expire.') }
        ],
        buttons: [
          { title: this._('Cancel'), icon: 'ban', handler: () => {} },
          {
            title: this._('Create key'),
            class: 'success',
            icon: 'key',
            requiredFields: ['label'],
            handler: (data) => {
              this.saveKey(data)
            }
          }
        ]
      })
    },
    async saveKey (data) {
      const kind = data.kind === 'publishable' ? 'publishable' : 'secret'
      const origins = kind === 'publishable'
        ? String(data.allowed_origins || '').split(/[\s,]+/).map(o => o.trim().replace(/\/+$/, '')).filter(o => o)
        : []
      let expiresAt = null
      if (data.expires_at instanceof Date) {
        expiresAt = DateTime.fromJSDate(data.expires_at, { zone: this.timezone }).endOf('day').toFormat('yyyy-LL-dd HH:mm:ss')
      }
      const result = await this.patchIntegration({
        operation: 'create_key',
        label: (data.label || '').trim(),
        kind,
        env: data.env === 'live' ? 'live' : 'test',
        allowed_origins: origins,
        expires_at: expiresAt
      })
      if (result && result.secret) {
        this.showModal('partnerkeysecret', PartnerKeySecret, {
          props: {
            secret: result.secret,
            keyLabel: result.key && result.key.label ? result.key.label : (data.label || '')
          },
          width: 640,
          clickToClose: false
        })
      } else if (result) {
        this.showError(this._('The key was created, but its secret was not returned. Revoke it and create a new one.'))
      }
    },
    revokeKey (key) {
      this.showDialog({
        title: this._('Revoke the key %s?').replace('%s', key.label || key.prefix),
        text: this._('Requests with the key are refused at once. This can\'t be undone.'),
        buttons: [
          { title: this._('Cancel'), icon: 'ban', handler: () => {} },
          {
            title: this._('Revoke'),
            class: 'danger',
            icon: 'ban',
            handler: () => {
              this.patchIntegration({ operation: 'revoke_key', key_id: key.id }, this._('The key was revoked.'))
            }
          }
        ]
      })
    },
    revokeConnection (connection) {
      this.showDialog({
        title: this._('Revoke the connection to %s?').replace('%s', connection.shop_name || connection.shop),
        text: this._('The integration loses its access to the shop at once. The shop can connect it again later.'),
        buttons: [
          { title: this._('Cancel'), icon: 'ban', handler: () => {} },
          {
            title: this._('Revoke'),
            class: 'danger',
            icon: 'ban',
            handler: () => {
              this.patchIntegration({ operation: 'revoke_connection', connection_id: connection.id }, this._('The connection was revoked.'))
            }
          }
        ]
      })
    },
    defaultUsageRange () {
      const today = DateTime.now().setZone(this.timezone).startOf('day')
      this.usageTo = today.toJSDate()
      this.usageFrom = today.minus({ days: USAGE_DAYS - 1 }).toJSDate()
    },
    usageDate (date) {
      return DateTime.fromJSDate(date, { zone: this.timezone }).toFormat('yyyy-LL-dd')
    },
    async loadUsage () {
      if (!this.integration.id || !this.usageFrom || !this.usageTo) {
        return
      }
      const id = this.integration.id
      this.usageLoading = true
      this.chartLoaded = false
      try {
        const { data } = await axios.get(this.integrationUrl('&view=usage&from=' + this.usageDate(this.usageFrom) + '&to=' + this.usageDate(this.usageTo)))
        if (id !== this.integration.id) {
          return
        }
        const usage = this.emptyUsage()
        for (const key of Object.keys(usage)) {
          usage[key] = data && Array.isArray(data[key]) ? data[key] : []
        }
        this.usage = usage
        this.usageLoadedFor = id
        // BarChart doesn't follow prop changes: mount it again for the new data
        this.chartKey++
        this.chartLoaded = usage.series.length > 0
      } catch (e) {
        this.showError(e, this._('Loading the usage failed.'), true)
      } finally {
        this.usageLoading = false
      }
    },
    formatSeriesDate (value) {
      const date = DateTime.fromISO(String(value || ''), { zone: this.timezone })
      return date.isValid ? date.toFormat('d.L.') : String(value || '')
    },
    formatMs (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const number = Number(value)
      return Number.isFinite(number) ? Math.round(number).toLocaleString(this.numberFormat || undefined) + ' ms' : ''
    },
    httpStatusClass (status) {
      const code = parseInt(status, 10)
      if (code === 429) {
        return 'label-primary'
      }
      if (code >= 500) {
        return 'label-danger'
      }
      if (code >= 400) {
        return 'label-warning'
      }
      return 'label-success'
    }
  },
  watch: {
    activeTab (newValue) {
      if (newValue === 'usage' && this.integration.id && this.usageLoadedFor !== this.integration.id) {
        this.loadUsage()
      }
    },
    usageFrom (newValue, oldValue) {
      if (oldValue && newValue && this.usageLoadedFor) {
        this.loadUsage()
      }
    },
    usageTo (newValue, oldValue) {
      if (oldValue && newValue && this.usageLoadedFor) {
        this.loadUsage()
      }
    }
  },
  components: {
    BarChart,
    DatePicker
  },
  created () {
    this.defaultUsageRange()
    this.loadIntegration(this.requestedId)
  },
  async updated () {
    const current = parseInt(this.integration.id || 0, 10)
    if (this.requestedId && current !== this.requestedId && !this.loading) {
      this.usage = this.emptyUsage()
      this.usageLoadedFor = 0
      this.chartLoaded = false
      await this.loadIntegration(this.requestedId)
    }
  }
}
</script>

<style scoped>
.partner-table-wrap {
  overflow-x: auto;
}
.partner-tab-buttons {
  margin: 10px 0;
}
.usage-range {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  margin: 10px 0;
}
.usage-range-picker {
  min-width: 160px;
}
.nowrap {
  white-space: nowrap;
}
h3 .label {
  font-size: 50%;
  vertical-align: middle;
}
</style>
